/**
 * The signed-in user (from /api/me), cached for offline launches.
 */
export interface MeUser {
  id: string
  email: string
  name: string
  avatarUrl?: string
}

export interface MeState {
  status: 'loading' | 'signed-in' | 'signed-out' | 'offline'
  user: MeUser | null
  owner: boolean
  members: Array<{ email: string; id: string }>
  vapidPublicKey: string | null
  devLogin: boolean
}

const KEY = 'tz.me'

function load(): MeState {
  try {
    const raw = localStorage.getItem(KEY)
    if (raw) {
      const cached = JSON.parse(raw) as MeState
      return { ...cached, status: 'loading' }
    }
  } catch {
    /* ignore */
  }
  return { status: 'loading', user: null, owner: false, members: [], vapidPublicKey: null, devLogin: false }
}

class Me {
  private state: MeState = load()
  private subs = new Set<() => void>()
  get = () => this.state
  subscribe = (fn: () => void) => {
    this.subs.add(fn)
    return () => this.subs.delete(fn)
  }
  private set(s: MeState) {
    this.state = s
    // Remembered for a fast start next time; a full (or private) storage just means no memory,
    // never a wrong status.
    try {
      if (s.user) localStorage.setItem(KEY, JSON.stringify(s))
    } catch {
      /* storage full or blocked */
    }
    for (const f of this.subs) f()
  }

  async refresh(): Promise<MeState> {
    try {
      const res = await fetch('/api/me', { credentials: 'same-origin' })
      if (res.status === 401) {
        this.set({ ...this.state, status: 'signed-out', user: null })
        try {
          localStorage.removeItem(KEY)
        } catch {
          /* blocked */
        }
        return this.state
      }
      if (!res.ok) throw new Error(String(res.status))
      const data = (await res.json()) as Omit<MeState, 'status'>
      this.set({ ...data, status: 'signed-in' })
    } catch {
      // Offline or server down: keep working with the cached identity.
      this.set({ ...this.state, status: this.state.user ? 'offline' : 'signed-out' })
    }
    return this.state
  }

  clear() {
    localStorage.removeItem(KEY)
    this.set({ status: 'signed-out', user: null, owner: false, members: [], vapidPublicKey: null, devLogin: false })
  }
}

export const me = new Me()

export function myId(): string {
  return me.get().user?.id ?? 'unknown'
}
